import React, { useEffect, useState, useRef } from 'react'
import Image from 'next/image'

const pad = (num) => (num < 10 ? `0${num}` : `${num}`)

const formatTime = (time) => {
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${pad(minutes)}:${pad(seconds)}`
}

const VideoPlayer = ({ src, subtitles }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.8)
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(true) // Enable by default
  const videoRef = useRef(null)
  const trackRef = useRef(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume
    }
  }, [volume])

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const handleProgressClick = (e) => {
    const rect = e.target.getBoundingClientRect()
    const clickPosition = e.clientX - rect.left
    const clickTime = (clickPosition / rect.width) * duration
    if (videoRef.current) {
      videoRef.current.currentTime = clickTime
    }
  }

  const handleVolumeClick = (e) => {
    const rect = e.target.getBoundingClientRect()
    const clickPosition = e.clientX - rect.left
    const newVolume = clickPosition / rect.width
    setVolume(Number(newVolume))
  }

  const toggleSubtitles = () => {
    if (trackRef.current) {
      trackRef.current.mode = subtitlesEnabled ? 'disabled' : 'showing'
      setSubtitlesEnabled(!subtitlesEnabled)
    }
  }

  return (
    <div className="videoPlayer">
      <video
        ref={videoRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onClick={handlePlayPause}
        style={{ cursor: 'pointer' }}
        controls={false}
      >
        <source src={src} type="video/mp4" />
        <track
          ref={trackRef}
          src={subtitles.replace('srt', 'vtt')}
          kind="subtitles"
          srcLang="en"
          label="English"
          default
        />
      </video>
      {!isPlaying && (
        <Image
          className="play"
          onClick={handlePlayPause}
          width={80}
          height={66}
          src={'/images/Play2.svg'}
          alt="Play"
        />
      )}
      <div className="controlsContainer">
        <div className="button" onClick={handlePlayPause}>
          <Image
            className="pause"
            width={9}
            height={10}
            src={isPlaying ? '/images/Pause.svg' : '/images/Play.svg'}
            alt={isPlaying ? 'Pause' : 'Play'}
          />
        </div>
        <div className="navigation">
          <span>{formatTime(currentTime)}</span>
          <div
            className="progress"
            onClick={handleProgressClick}
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${
                (currentTime / duration) * 100 || 0
              }%, #FFFFFF1a ${(currentTime / duration) * 100 || 0}%, #FFFFFF1a 100%)`,
            }}
          ></div>
          <span>{formatTime(duration)}</span>
          <div
            className="volume"
            onClick={handleVolumeClick}
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${
                volume * 100
              }%, #FFFFFF1a ${volume * 100}%, #FFFFFF1a 100%)`,
            }}
          ></div>
        </div>
        <div className="button" onClick={toggleSubtitles} style={{ marginRight: 5 }}>
          <Image
            className="subtitles"
            width={25}
            height={25}
            src={
              subtitlesEnabled
                ? '/images/onboarding/SubtitlesOn.png'
                : '/images/onboarding/SubtitlesOff.png'
            }
            alt="Subtitles"
          />
        </div>
        <div className="button" onClick={() => videoRef.current.requestFullscreen()}>
          <Image
            className="expand"
            width={16}
            height={15}
            src="/images/Expand.svg"
            alt="Expand"
          />
        </div>
      </div>
    </div>
  )
}

export const FullVideoPlayer = ({ content }) => {
  const [speakers, setSpeakers] = useState([])
  const [transcript, setTranscript] = useState([])
  const [speakerIndex, setSpeakerIndex] = useState(0)
  const articleRef = useRef()

  useEffect(() => {
    const speak = Array.from(articleRef.current?.querySelectorAll('strong') || [])
    setTranscript(
      Array.from(articleRef.current?.querySelectorAll('p') || []).filter(
        (p) => !['strong', 'br'].includes(p.childNodes[0]?.localName)
      )
    )
    setSpeakers(speak)
  }, [])

  return (
    <section className="videoblog">
      <div className="videosection">
        <div className="videoleft" style={{ width: '100%', maxWidth: 1300 }}>
          <VideoPlayer src={content.video} subtitles={content.subtitles} />
          <h1 className="title" style={{ textAlign: 'center' }}>
            {content.title}
          </h1>
        </div>
      </div>
      <div className="summary">
        <span className="resume" style={{ margin: '22px 0' }}>
          {content.sumup}
        </span>
      </div>
    </section>
  )
}
